<template>
  <div class="animated fadeIn list-of-eliminatecancel">
    <b-card>
      <div slot="header">
        <i class="fa fa-balance-scale mr-1"></i>
        <strong>Eliminated Cancelation Invoice</strong> (AR Invoice vs Cancelation)
        <div class="card-header-actions">
          <b-button variant="success" size="sm" class="mr-1" :disabled="isLoading || pairsList.length === 0" @click="showConfirmModal = true">
            <i class="fa fa-check-square-o"></i> Proses Eliminasi
          </b-button>
          <b-button variant="primary" size="sm" class="mr-1" :disabled="pairsList.length === 0" @click="exportData">
            <i class="fa fa-file-excel-o"></i> Export
          </b-button>
          <b-button variant="secondary" size="sm" class="mr-1" @click="close">
            <i class="fa fa-close"></i> Close
          </b-button>
        </div>
      </div>

      <!-- Filter Section -->
      <b-row class="mb-3">
        <b-col lg="6" md="8">
          <b-input-group>
            <b-input-group-prepend>
              <span class="input-group-text"><i class="fa fa-calendar mr-1"></i> Inv. Date</span>
            </b-input-group-prepend>
            <b-form-input v-model="model.tgl1" type="date" placeholder="Tanggal Awal"></b-form-input>
            <b-input-group-prepend>
              <span class="input-group-text">s/d</span>
            </b-input-group-prepend>
            <b-form-input v-model="model.tgl2" type="date" placeholder="Tanggal Akhir"></b-form-input>
            <b-input-group-append>
              <b-button variant="primary" :disabled="isLoading" @click="getPreview">
                <i class="fa fa-search"></i> Cari Pasangan (Preview)
              </b-button>
              <b-button variant="outline-secondary" @click="resetFilter" title="Reset Filter">
                <i class="fa fa-refresh"></i>
              </b-button>
            </b-input-group-append>
          </b-input-group>
          <small class="text-muted mt-1 d-block">
            * Kosongkan filter tanggal untuk memproses seluruh invoice pada tenant / perusahaan aktif.
          </small>
        </b-col>

        <b-col lg="6" md="4" class="text-right d-flex align-items-center justify-content-end">
          <b-badge v-if="hasSearched && isExecuted" variant="success" class="p-2 font-size-sm">
            <i class="fa fa-check-circle"></i> Status: Database Updated
          </b-badge>
          <b-badge v-else-if="hasSearched && !isExecuted" variant="info" class="p-2 font-size-sm">
            <i class="fa fa-info-circle"></i> Status: Mode Preview (Read-Only)
          </b-badge>
        </b-col>
      </b-row>

      <!-- Summary Statistics Cards -->
      <b-row v-if="hasSearched" class="mb-3">
        <b-col sm="6" lg="3">
          <b-card bg-variant="light" class="mb-2 text-center py-1">
            <div class="text-muted small text-uppercase font-weight-bold">Total Pasangan Ditemukan</div>
            <div class="h4 mb-0 text-primary font-weight-bold">{{ pairsList.length }}</div>
          </b-card>
        </b-col>
        <b-col sm="6" lg="3">
          <b-card bg-variant="light" class="mb-2 text-center py-1">
            <div class="text-muted small text-uppercase font-weight-bold">Total Invoice Original</div>
            <div class="h5 mb-0 text-dark font-weight-bold">{{ totalInvoiceAmt | numFormat }}</div>
          </b-card>
        </b-col>
        <b-col sm="6" lg="3">
          <b-card bg-variant="light" class="mb-2 text-center py-1">
            <div class="text-muted small text-uppercase font-weight-bold">Total Cancelation</div>
            <div class="h5 mb-0 text-danger font-weight-bold">{{ totalCancelAmt | numFormat }}</div>
          </b-card>
        </b-col>
        <b-col sm="6" lg="3">
          <b-card bg-variant="light" class="mb-2 text-center py-1">
            <div class="text-muted small text-uppercase font-weight-bold">Net Balance (Selisih)</div>
            <div class="h5 mb-0 font-weight-bold" :class="netBalance === 0 ? 'text-success' : 'text-warning'">
              {{ netBalance | numFormat }}
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Table Section -->
      <b-card no-body>
        <template v-slot:header>
          <b-row align-v="center">
            <b-col md="4">
              <b-input-group size="sm">
                <b-input-group-prepend>
                  <span class="input-group-text"><i class="fa fa-filter"></i></span>
                </b-input-group-prepend>
                <b-form-input v-model="filterKeyword" type="search" placeholder="Cari nomor invoice / base invoice..."></b-form-input>
                <b-input-group-append v-if="filterKeyword">
                  <b-button size="sm" @click="filterKeyword = ''">Clear</b-button>
                </b-input-group-append>
              </b-input-group>
            </b-col>
            <b-col md="8" class="text-right">
              <span class="text-muted small">
                Menampilkan <strong>{{ filteredCount }}</strong> data pasangan
              </span>
            </b-col>
          </b-row>
        </template>

        <b-table
          class="mb-0"
          ref="tablePairs"
          sticky-header="600px"
          outlined
          responsive
          striped
          hover
          small
          :items="formattedPairs"
          :fields="tblFields"
          :current-page="tblPagination.page"
          :per-page="tblPagination.limit"
          :filter="filterKeyword"
          @filtered="onFiltered"
          show-empty
          empty-text="Belum ada data atau tidak ditemukan pasangan invoice cancelation untuk kriteria filter ini."
        >
          <!-- Top Multi-level Header -->
          <template v-slot:thead-top>
            <b-tr>
              <b-th colspan="2" class="text-center bg-light border">INFORMASI DASAR</b-th>
              <b-th colspan="3" class="text-center bg-primary text-white border">INVOICE ORIGINAL</b-th>
              <b-th colspan="3" class="text-center bg-danger text-white border">INVOICE CANCELATION</b-th>
              <b-th colspan="2" class="text-center bg-success text-white border">HASIL ELIMINASI</b-th>
            </b-tr>
          </template>

          <!-- Index Column -->
          <template v-slot:cell(index)="data">
            <span class="text-muted">{{ (tblPagination.page - 1) * tblPagination.limit + data.index + 1 }}</span>
          </template>

          <!-- Base Invoice No -->
          <template v-slot:cell(baseInvoiceno)="data">
            <strong>{{ data.item.baseInvoiceno }}</strong>
          </template>

          <!-- Invoice Date -->
          <template v-slot:cell(inv_date)="data">
            <span>{{ data.item.inv_date | moment("DD-MMM-YYYY") }}</span>
          </template>

          <!-- Invoice Amount -->
          <template v-slot:cell(inv_amt)="data">
            <span class="text-right font-weight-bold text-primary">{{ data.item.inv_amt | numFormat }}</span>
          </template>

          <!-- Cancel Date -->
          <template v-slot:cell(cancel_date)="data">
            <span>{{ data.item.cancel_date | moment("DD-MMM-YYYY") }}</span>
          </template>

          <!-- Cancel Amount -->
          <template v-slot:cell(cancel_amt)="data">
            <span class="text-right font-weight-bold text-danger">{{ data.item.cancel_amt | numFormat }}</span>
          </template>

          <!-- Target Close Date -->
          <template v-slot:cell(closeDate)="data">
            <b-badge variant="dark" class="p-1 font-weight-normal">
              {{ data.item.closeDate | moment("DD-MMM-YYYY") }}
            </b-badge>
          </template>

          <!-- Status Column -->
          <template v-slot:cell(status)="data">
            <b-badge v-if="isExecuted" variant="success">
              <i class="fa fa-check"></i> Canceled & Closed
            </b-badge>
            <b-badge v-else variant="warning">
              <i class="fa fa-clock-o"></i> Siap Dieliminasi
            </b-badge>
          </template>
        </b-table>

        <!-- Pagination and Per Page Options -->
        <div class="p-2 border-top d-flex justify-content-between align-items-center flex-wrap" v-if="pairsList.length > 0">
          <div class="d-flex align-items-center mb-2 mb-md-0">
            <span class="mr-2 small text-muted">Per halaman:</span>
            <b-form-select v-model="tblPagination.limit" size="sm" :options="[10, 20, 50, 100]" style="width: 75px;"></b-form-select>
            <span class="ml-3 small text-muted">
              Total <strong>{{ pairsList.length }}</strong> data pasangan
            </span>
          </div>
          <b-pagination
            class="mb-0"
            size="sm"
            v-model="tblPagination.page"
            :total-rows="filteredCount"
            :per-page="tblPagination.limit"
            align="right"
          ></b-pagination>
        </div>
      </b-card>
    </b-card>

    <!-- Confirmation Modal for Elimination Execution -->
    <b-modal
      v-model="showConfirmModal"
      title="Konfirmasi Eliminasi Cancelation"
      header-bg-variant="warning"
      header-text-variant="dark"
      centered
      @ok="executeElimination"
    >
      <div class="p-2">
        <p class="mb-2">
          Anda akan memproses eliminasi untuk <strong>{{ pairsList.length }} pasang</strong> Invoice & Cancelation.
        </p>
        <b-alert show variant="danger" class="mb-0 py-2">
          <i class="fa fa-exclamation-triangle mr-1"></i>
          <strong>Perhatian:</strong>
          Proses ini akan mengupdate tabel <code>arinvtt</code> dengan:
          <ul class="mb-0 pl-3 mt-1">
            <li><code>canceled = 1</code></li>
            <li><code>closedate = &lt;Tanggal Invoice Termuda&gt;</code></li>
          </ul>
        </b-alert>
      </div>
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="secondary" size="sm" @click="cancel()">Batal</b-button>
        <b-button variant="danger" size="sm" @click="ok()">
          <i class="fa fa-trash-o"></i> Ya, Eksekusi Eliminasi
        </b-button>
      </template>
    </b-modal>

    <!-- Loading Spinner Overlay -->
    <loading :active.sync="isLoading" :height="128" :width="128" color="#4b8cf4"></loading>
  </div>
</template>

<script>
import moment from 'moment';
import arreport from '../../apis/arreport';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import toastr from 'mini-toastr';

toastr.init();

export default {
  name: 'EliminatedCancelationInvoice',
  components: {
    Loading,
  },
  data() {
    return {
      isLoading: false,
      hasSearched: false,
      isExecuted: false,
      showConfirmModal: false,
      filterKeyword: '',
      filteredCount: 0,
      model: {
        tgl1: '',
        tgl2: '',
      },
      pairsList: [],
      tblPagination: {
        page: 1,
        limit: 20,
      },
      tblFields: [
        {
          key: 'index',
          label: 'No.',
          thClass: 'text-center',
          tdClass: 'text-center align-middle',
          thStyle: 'width: 50px',
        },
        {
          key: 'baseInvoiceno',
          label: 'Base Inv #',
          sortable: true,
          tdClass: 'align-middle font-weight-bold',
        },
        {
          key: 'inv_no',
          label: 'Inv. Number',
          sortable: true,
          tdClass: 'align-middle',
        },
        {
          key: 'inv_date',
          label: 'Inv. Date',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center align-middle',
          thStyle: 'width: 110px',
        },
        {
          key: 'inv_amt',
          label: 'Amount Due',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right align-middle',
        },
        {
          key: 'cancel_no',
          label: 'Cancel Number',
          sortable: true,
          tdClass: 'align-middle',
        },
        {
          key: 'cancel_date',
          label: 'Cancel Date',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center align-middle',
          thStyle: 'width: 110px',
        },
        {
          key: 'cancel_amt',
          label: 'Amount Due',
          sortable: true,
          thClass: 'text-right',
          tdClass: 'text-right align-middle',
        },
        {
          key: 'closeDate',
          label: 'Close Date',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center align-middle',
          thStyle: 'width: 110px',
        },
        {
          key: 'status',
          label: 'Status',
          thClass: 'text-center',
          tdClass: 'text-center align-middle',
          thStyle: 'width: 130px',
        },
      ],
    };
  },
  computed: {
    formattedPairs() {
      return this.pairsList.map((item, idx) => ({
        id: idx,
        baseInvoiceno: item.baseInvoiceno,
        closeDate: item.closeDate,
        inv_no: item.invoice ? item.invoice.invoiceno : '',
        inv_date: item.invoice ? item.invoice.invdate : '',
        inv_amt: item.invoice ? item.invoice.ttlamtdue : 0,
        cancel_no: item.cancelation ? item.cancelation.invoiceno : '',
        cancel_date: item.cancelation ? item.cancelation.invdate : '',
        cancel_amt: item.cancelation ? item.cancelation.ttlamtdue : 0,
      }));
    },
    totalInvoiceAmt() {
      return this.pairsList.reduce((acc, curr) => acc + (curr.invoice && curr.invoice.ttlamtdue ? Number(curr.invoice.ttlamtdue) : 0), 0);
    },
    totalCancelAmt() {
      return this.pairsList.reduce((acc, curr) => acc + (curr.cancelation && curr.cancelation.ttlamtdue ? Number(curr.cancelation.ttlamtdue) : 0), 0);
    },
    netBalance() {
      return this.totalInvoiceAmt + this.totalCancelAmt;
    },
  },
  watch: {
    formattedPairs: {
      immediate: true,
      handler(val) {
        this.filteredCount = val.length;
      },
    },
  },
  methods: {
    onFiltered(filteredItems) {
      this.filteredCount = filteredItems.length;
      this.tblPagination.page = 1;
    },
    resetFilter() {
      this.model.tgl1 = '';
      this.model.tgl2 = '';
      this.filterKeyword = '';
      this.pairsList = [];
      this.hasSearched = false;
      this.isExecuted = false;
    },
    async getPreview() {
      this.isLoading = true;
      this.isExecuted = false;
      try {
        const payload = {
          previewOnly: true,
        };
        if (this.model.tgl1) payload.tgl1 = this.model.tgl1;
        if (this.model.tgl2) payload.tgl2 = this.model.tgl2;

        const response = await arreport.eliminatecancel(payload);
        this.pairsList = response.pairs || [];
        this.hasSearched = true;
        this.tblPagination.page = 1;

        if (this.pairsList.length === 0) {
          toastr.info('Tidak ditemukan pasangan invoice dan cancelation yang cocok.');
        } else {
          toastr.success(`Ditemukan ${this.pairsList.length} pasangan invoice & cancelation.`);
        }
      } catch (error) {
        const msg = (error && error.message) || (error && error.error) || 'Gagal mengambil data preview eliminasi';
        toastr.error(msg);
      } finally {
        this.isLoading = false;
      }
    },
    async executeElimination() {
      if (this.pairsList.length === 0) {
        toastr.warning('Tidak ada data pasangan untuk dieliminasi.');
        return;
      }

      this.isLoading = true;
      try {
        const payload = {
          previewOnly: false,
        };
        if (this.model.tgl1) payload.tgl1 = this.model.tgl1;
        if (this.model.tgl2) payload.tgl2 = this.model.tgl2;

        const response = await arreport.eliminatecancel(payload);
        this.isExecuted = true;
        toastr.success(`Berhasil mengeliminasi ${response.count || this.pairsList.length} pasangan invoice & cancelation.`);
      } catch (error) {
        const msg = (error && error.message) || (error && error.error) || 'Gagal mengeksekusi proses eliminasi';
        toastr.error(msg);
      } finally {
        this.isLoading = false;
      }
    },
    exportData() {
      if (this.formattedPairs.length === 0) {
        toastr.warning('Tidak ada data untuk diexport.');
        return;
      }

      try {
        const headers = [
          'No',
          'Base Invoice No',
          'Invoice No',
          'Invoice Date',
          'Invoice Amount',
          'Cancelation No',
          'Cancelation Date',
          'Cancelation Amount',
          'Target Close Date',
          'Status',
        ];

        const rows = this.formattedPairs.map((item, idx) => [
          idx + 1,
          `"${item.baseInvoiceno}"`,
          `"${item.inv_no}"`,
          item.inv_date ? moment(item.inv_date).format('YYYY-MM-DD') : '',
          item.inv_amt,
          `"${item.cancel_no}"`,
          item.cancel_date ? moment(item.cancel_date).format('YYYY-MM-DD') : '',
          item.cancel_amt,
          item.closeDate ? moment(item.closeDate).format('YYYY-MM-DD') : '',
          this.isExecuted ? 'Canceled & Closed' : 'Ready to Eliminate',
        ]);

        const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `eliminated_cancel_invoice_${moment().format('YYYYMMDD_HHmmss')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toastr.success('File CSV berhasil didownload.');
      } catch (error) {
        toastr.error('Gagal mengekspor data.');
      }
    },
    close() {
      this.$router.push('/receivable/home');
    },
  },
};
</script>

<style lang="scss" scoped>
.list-of-eliminatecancel {
  .card-header-actions {
    float: right;
  }
}
</style>
